import { Checkbox, HStack, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { getOptionTranslation, NumberOfLanesType } from "@keita/lanepatrol-sdk";
import { generateCraOptions, SelectOption } from "./utils";
import { useStores } from "../../../../shared/stores/RootStore";
import { ReactNode } from "react";
import { VerboseSelectOptions } from "./VerboseSelectOptions";
import { PresenceDistance } from "../../../models/HlaLane";
import { t } from "i18next";

type Side = "left" | "right";

const AdjacentFeaturesFormBase = ({ canEdit }: { canEdit: boolean }) => {
  const { hlaStore } = useStores();
  const lane = hlaStore.getSelectedLane()!;
  const directionkey = lane.frontFacing ? "frontFacingLane" : "backFacingLane";

  const bySide = (side: Side) =>
    side === "left" ? lane.adjacentFeaturesLeft : lane.adjacentFeaturesRight;

  const setValue = (side: Side, name: string, value: any) => {
    const sideKey =
      side === "left" ? "adjacentFeaturesLeft" : "adjacentFeaturesRight";

    hlaStore.code((prev) => {
      prev[directionkey]![sideKey] = {
        ...prev[directionkey]![sideKey],
        [name]: value,
      };
      return { ...prev };
    });
  };

  const setAdjacentFeaturesDependencies = (
    side: Side,
    name: string,
    value: PresenceDistance | null,
  ) => {
    const adjacentFeatures = bySide(side);

    if (value) {
      if (value === PresenceDistance.From0to1m) {
        if (
          name !== "roadLane" &&
          adjacentFeatures.roadLane === PresenceDistance.From0to1m
        ) {
          setValue(side, "roadLane", null);
        }
        if (
          name !== "vehicleParking" &&
          adjacentFeatures.vehicleParking === PresenceDistance.From0to1m
        ) {
          setValue(side, "vehicleParking", null);
        }
        if (
          name !== "sidewalk" &&
          adjacentFeatures.sidewalk === PresenceDistance.From0to1m
        ) {
          setValue(side, "sidewalk", null);
        }
      }
      if (value === PresenceDistance.From1to3m) {
        if (
          name !== "roadLane" &&
          adjacentFeatures.roadLane === PresenceDistance.From1to3m
        ) {
          setValue(side, "roadLane", null);
        }
        if (
          name !== "vehicleParking" &&
          adjacentFeatures.vehicleParking === PresenceDistance.From1to3m
        ) {
          setValue(side, "vehicleParking", null);
        }
        if (
          name !== "sidewalk" &&
          adjacentFeatures.sidewalk === PresenceDistance.From1to3m
        ) {
          setValue(side, "sidewalk", null);
        }
      }
    }
  };

  return (
    <Stack flexBasis={1} flexGrow={1}>
      <Field label={t(`shared:hlaAttribute.hardSegregation`)}>
        {(side) => (
          <Checkbox.Root
            disabled={!canEdit}
            cursor={!canEdit ? "auto" : "pointer"}
            colorPalette="primary"
            checked={bySide(side).hardSegregation}
            onCheckedChange={({ checked }) => {
              setValue(side, "hardSegregation", checked);
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        )}
      </Field>

      <Field label="Road lane">
        {(side) => {
          const adjacentFeatures = bySide(side);
          const options = presenceDistanceOptions;
          return (
            <VerboseSelectOptions
              nullable
              reversed={side === "left"}
              disabled={!canEdit}
              value={adjacentFeatures.roadLane}
              setValue={(value) => {
                setValue(side, "roadLane", value);
                setAdjacentFeaturesDependencies(side, "roadLane", value);
              }}
              options={options}
            />
          );
        }}
      </Field>

      <Field label="Number of lanes">
        {(side) =>
          bySide(side).roadLane == null ? (
            <HStack h={7} alignItems={"center"}>
              <Text fontSize={"xs"}>N/A</Text>
            </HStack>
          ) : (
            <VerboseSelectOptions
              reversed={side === "left"}
              disabled={!canEdit}
              value={bySide(side).numberOfLanes}
              setValue={(value) => {
                setValue(side, "numberOfLanes", value);
              }}
              options={generateCraOptions(Object.values(NumberOfLanesType))}
            />
          )
        }
      </Field>

      <Field label="Vehicle parking">
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).vehicleParking}
            setValue={(value) => {
              setValue(side, "vehicleParking", value);
              setAdjacentFeaturesDependencies(side, "vehicleParking", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>

      <Field label="Sidewalk">
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).sidewalk}
            setValue={(value) => {
              setValue(side, "sidewalk", value);
              setAdjacentFeaturesDependencies(side, "sidewalk", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>

      <Field label="Object or level change">
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).objectOrLevelChange}
            setValue={(value) => {
              setValue(side, "objectOrLevelChange", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>

      <Field label="Severe hazard">
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).severeHazard}
            setValue={(value) => {
              setValue(side, "severeHazard", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>
    </Stack>
  );
};

const presenceDistanceOptions: SelectOption[] = [
  {
    value: PresenceDistance.From0to1m,
    translation: getOptionTranslation(PresenceDistance.From0to1m),
  },
  {
    value: PresenceDistance.From1to3m,
    translation: getOptionTranslation(PresenceDistance.From1to3m),
  },
];

const Field = ({
  label,
  children,
}: {
  label: string;
  children: (side: Side) => ReactNode;
}) => {
  return (
    <HStack justifyContent={"center"}>
      <HStack justifyContent={"right"} flexBasis={1} flexGrow={1}>
        {children("left")}
      </HStack>

      <Text
        flexShrink={0}
        textAlign={"center"}
        w="200px"
        fontSize={"xs"}
        fontWeight={"semibold"}
      >
        {label}
      </Text>

      <HStack justifyContent={"left"} flexBasis={1} flexGrow={1}>
        {children("right")}
      </HStack>
    </HStack>
  );
};

export const AdjacentFeaturesForm = observer(AdjacentFeaturesFormBase);
